import api from '@/http/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ErrorComponent from '@univdiam/ui/customComponents/ErrorComponent';
import StaffDetailsView from '@univdiam/ui/customComponents/Staff/StaffDetailsView';
import type { StaffRecord } from '@univdiam/ui/customComponents/Staff/StaffTable';
import type { StaffActivityRecord } from '@univdiam/ui/customComponents/Staff/StaffDetailsView';
import axios from 'axios';
import { LoaderCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import TicketIcon from '@/assets/FeaturedIcon.svg';
import { useDeactivateStaff } from '@/sharedData/Staff';
import { toast } from 'sonner';
import { useAuthStore } from '@/store';

const getSingleStaff = (staffId: string) => api.get(`/admin/staff/${staffId}`);
const getStaffActivity = (staffId: string) => api.get(`/admin/staff/${staffId}/activity`);

const StaffDetails = () => {
    const { staffId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuthStore();

    const { data, isPending: isStaffLoading, isError, error } = useQuery({
        queryKey: ['singleStaff', staffId],
        queryFn: () => getSingleStaff(staffId!),
        enabled: !!staffId
    });

    const { data: activity } = useQuery({
        queryKey: ['singleStaffActivity', staffId],
        queryFn: () => getStaffActivity(staffId!),
        enabled: !!staffId
    });

    const staff = data?.data?.staff as StaffRecord;
    const staffActivity = activity?.data?.logs as StaffActivityRecord[];

    const { mutate: deactivateStaff } = useDeactivateStaff();

    const handleToggleActive = () => {
        deactivateStaff({ staffId: staff.id, status: staff.is_active ? 0 : 1 }, {
            onSuccess: () => {
                toast.success(staff.is_active ? 'Staff deactivated!' : 'Staff activated!');
                queryClient.invalidateQueries({ queryKey: ['singleStaff'] });
            },
        });
    };

    const isAxiosError = axios.isAxiosError(error);

    if (isStaffLoading) {
        return <LoaderCircle className="animate-spin" />;
    }

    if (isError) {
        return (
            <div className='mt-[16px] mx-[16px]'>
                <ErrorComponent message={isAxiosError ? error?.response?.data?.error : 'Something went wrong!'} />
            </div>
        );
    }

    return (
        <StaffDetailsView
            icon={TicketIcon}
            staff={staff}
            activity={staffActivity}
            showOrders={false}
            canManage={user?.role === 'ADMIN'}
            onBack={() => navigate(-1)}
            onToggleActive={handleToggleActive}
        />
    );
};

export default StaffDetails;
